// The ONE artifact action list every artifact pane hands its header
// (#11548 r60, #12091, #12481, #13261), in one order, so the panes cannot
// drift apart: a draft's Save, Chat, Share, the pane's Export or Download,
// Copy link, Link to record, the pane's hand edit, the pane's Copy. The
// header keeps Save, Chat, Share and Export/Download as buttons, lists every
// action but Chat in the title's menu, and puts Version history, Comments
// and Close in place itself.
import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { Link, MessageSquareText, Paperclip, Save, Share2 } from "lucide-react";
import { ShareModal } from "@/components/common/ShareModal";
import AssignArtifactDialog from "../FpPages/Artifacts/AssignArtifactDialog";
import { useArtifactHistory } from "./artifactHistory";
import { useArtifactHistoryStore } from "./artifactHistoryStore";
import { artifactLink } from "../FpPages/Artifacts/artifactSlug";
import type { PaneAction } from "./ArtifactPaneHeader";
import {
  artifactChatTarget,
  canAssignArtifact,
  saveArtifact,
  type ArtifactItem,
} from "../FpPages/Artifacts/useArtifacts";
import {
  artifactIsOnItsChat,
  artifactItemKey,
  useArtifactItem,
} from "./ArtifactSources";
import { useFlash } from "@/hooks/productTimerHooks";

interface UseArtifactPanelActionsOptions {
  /** Null for a pane with nothing in the registry behind it: no actions. */
  artifactId: string | null;
  title: string;
  /** The pane's own file action: Export (a new file from what is on
   *  screen) or Download (the stored or serialized file). A button beside
   *  Share, and in the title's menu after it. */
  file?: PaneAction;
  /** The pane's own hand edit (Edit text, Edit table, Edit source), when
   *  the kind is hand-editable: in the title's menu. */
  edit?: PaneAction;
  /** The pane's own Copy: in the title's menu, after the hand edit. */
  copy?: PaneAction;
}

/** The list, in order, plus the dialogs it opens. With version history open
 *  every action stands down — sharing or chatting about "version 5 of 10"
 *  is ambiguous — and only the header's own toggle that closes it stays
 *  (#11819). */
export const useArtifactPanelActions = ({
  artifactId,
  title,
  file,
  edit,
  copy,
}: UseArtifactPanelActionsOptions): {
  actions: PaneAction[];
  dialogs: React.ReactNode;
} => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const history = useArtifactHistory(artifactId);
  // The artifact's registry read, shared with the pane: where it came from
  // decides Chat. Nothing is offered until it has answered (no flash of a
  // button that then leaves); a failed read still offers the new chat.
  const item = useArtifactItem(artifactId);
  const queryClient = useQueryClient();
  const [shareOpen, setShareOpen] = useState(false);
  const [linkCopied, triggerLinkCopied] = useFlash(2000);
  const [assignOpen, setAssignOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Draft state (#11548 r75): a chat creation the user hasn't saved yet
  // wears a Save button at the cluster's head. The same read answers it
  // and whether the caller may assign the artifact to records; both false
  // until it lands, so neither button flashes.
  const draft = Boolean(item.data?.draft && item.data.created_by_me);
  const canAssign = item.data ? canAssignArtifact(item.data) : false;
  // Read again when the chat reports this artifact updated: an agent's
  // write can save the draft while the pane is open (linking a table's
  // names to records — #12430).
  React.useEffect(() => {
    if (artifactId === null) return;
    const onUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ artifactId?: string }>).detail;
      if (detail?.artifactId === artifactId) {
        void queryClient.invalidateQueries({
          queryKey: artifactItemKey(artifactId),
        });
      }
    };
    window.addEventListener("artifactUpdated", onUpdated);
    return () => window.removeEventListener("artifactUpdated", onUpdated);
  }, [artifactId, queryClient]);
  const save = () => {
    if (saving || artifactId === null) return;
    setSaving(true);
    saveArtifact(artifactId)
      .then(() =>
        queryClient.setQueryData<ArtifactItem>(
          artifactItemKey(artifactId),
          (read) => (read ? { ...read, draft: false } : read)
        )
      )
      .catch((error: unknown) =>
        console.error("Saving the artifact failed", error)
      )
      .finally(() => setSaving(false));
  };

  const copyLink = () => {
    if (artifactId === null) return;
    void navigator.clipboard
      .writeText(artifactLink(artifactId))
      .then(() => triggerLinkCopied());
  };

  // Chat (#13261): the conversation that made the artifact, opened with
  // the artifact beside it, or a new chat with an empty composer. Not on
  // that conversation itself, where the composer already is the way to
  // talk about it.
  // Nor on the new chat an artifact's Chat opened it in: the reader is
  // already in the chat Chat would open.
  const handoff = useArtifactHistoryStore((state) => state.chatHandoff);
  const itemSettled = item.isSuccess || item.isError;
  const onItsChat =
    (handoff !== null &&
      handoff.artifactId === artifactId &&
      handoff.pathname === pathname) ||
    (item.data !== undefined &&
      artifactIsOnItsChat(
        {
          sourceKind: item.data.source_kind,
          sourceConversationId: item.data.source_conversation_id,
        },
        pathname
      ));
  const openChat = () => {
    if (artifactId === null) return;
    const { to, state } = artifactChatTarget(item.data ?? null, artifactId);
    navigate(to, { state });
  };

  const actions: PaneAction[] = history.open
    ? []
    : artifactId === null
      ? // Nothing in the registry behind the pane: its own file action and
        // Copy, in the same order, and no hand edit (there is no row to
        // save it to).
        [
          ...(file ? [{ ...file, pinned: true }] : []),
          ...(copy ? [{ ...copy, secondary: true }] : []),
        ]
      : [
          ...(draft
            ? [
                {
                  key: "save",
                  label: saving ? "Saving…" : "Save to library",
                  buttonLabel: saving ? "Saving…" : "Save",
                  hint: "Until saved, this stays only in this chat",
                  icon: Save,
                  // The one filled control: what a draft asks for. Ink,
                  // not blue (Gerry, after trying the accent).
                  emphasis: true,
                  disabled: saving,
                  onSelect: save,
                } satisfies PaneAction,
              ]
            : []),
          ...(itemSettled && !onItsChat
            ? [
                {
                  key: "chat",
                  label: "Chat",
                  icon: MessageSquareText,
                  word: true,
                  buttonOnly: true,
                  onSelect: openChat,
                } satisfies PaneAction,
              ]
            : []),
          {
            key: "share",
            label: "Share",
            icon: Share2,
            pinned: true,
            onSelect: () => setShareOpen(true),
          },
          ...(file ? [{ ...file, pinned: true }] : []),
          {
            key: "copy-link",
            label: "Copy link",
            hint: linkCopied ? "Copied!" : "Copy link",
            icon: Link,
            secondary: true,
            onSelect: copyLink,
          },
          ...(canAssign
            ? [
                {
                  key: "assign",
                  label: "Link to record",
                  icon: Paperclip,
                  secondary: true,
                  onSelect: () => setAssignOpen(true),
                } satisfies PaneAction,
              ]
            : []),
          ...(edit ? [{ ...edit, secondary: true }] : []),
          ...(copy ? [{ ...copy, secondary: true }] : []),
        ];

  const dialogs =
    artifactId === null ? null : (
      <>
        {shareOpen && (
          <ShareModal
            open
            onClose={() => setShareOpen(false)}
            entityType="artifact"
            entityId={artifactId}
            entityName={title}
            onCopyLink={copyLink}
          />
        )}
        {assignOpen && (
          <AssignArtifactDialog
            artifactId={artifactId}
            artifactTitle={title}
            onClose={() => setAssignOpen(false)}
          />
        )}
      </>
    );

  return { actions, dialogs };
};
